import type { SupportedLocale } from '@stm/contracts';
import { FamilyCalendar } from './FamilyCalendar';
import { StaffCalendar } from './StaffCalendar';
import { VenuesSection } from './VenuesSection';
import { TaskSection } from './TaskSection';

export interface CalendarPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** Action permissions of the signed-in person in this club; the server enforces them as well. */
  permissions: readonly string[];
  initialEventId?: string | undefined;
}

/**
 * The Calendar page. Staff see the club calendar of their departments; guardians and adult athletes
 * see the published events of their own athletes with an answer control. A person can hold both.
 */
export function CalendarPanel({
  locale,
  organizationId,
  csrfToken,
  permissions,
  initialEventId,
}: CalendarPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}`;
  const canBrowse = permissions.includes('events.browse');
  return (
    <>
      {canBrowse && (
        <StaffCalendar
          locale={locale}
          base={base}
          csrfToken={csrfToken}
          canManage={permissions.includes('events.manage')}
          canRecord={permissions.includes('attendance.record')}
          canAttachPlan={permissions.includes('training.view')}
          initialEventId={initialEventId}
        />
      )}
      {permissions.includes('schedule.own') && (
        <FamilyCalendar locale={locale} base={base} csrfToken={csrfToken} />
      )}
      {canBrowse && (
        <TaskSection locale={locale} titleKey="calendar.venues.title">
          <VenuesSection
            locale={locale}
            base={base}
            csrfToken={csrfToken}
            canManage={permissions.includes('resources.manage')}
          />
        </TaskSection>
      )}
    </>
  );
}
